/** @jsxImportSource theme-ui */

import React, { useEffect, useState } from "react"
import _ from "lodash"
import { Box } from "theme-ui"
import { Helmet } from "react-helmet"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Color from "color"
import Select from "react-select"
import {
  faCog,
  faPaperPlane,
  faPaperclip
} from "@fortawesome/free-solid-svg-icons"

import * as Styles from "./WebchatPreviewStyles"
import PersistentMenu from "../PersistentMenu/PersistentMenu"
import BusinessInfoPreview from "../BusinessInfoPreview/BusinessInfoPreview"
const LUM_THRESHOLD = 0.6

const WebchatPreview = ({
  info, 
  cssString,
}) => {
  const defaultLg = info?.localeGroups.find(o => o.default) || {}

  const [showSettings, setShowSettings] = useState(false)
  const [headerShowOverflow, setHeaderShowOverflow] = useState(false)
  const [headerShowProfileOverflow, setHeaderShowProfileOverflow] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)
  const [fontSize, setFontSize] = useState("16")
  const [selectedLocaleGroup, setSelectedLocaleGroup] = useState(defaultLg)
  const [selectedLocalised, setSelectedLocalised] = useState({})
  const themeColor = _.get(info, "chatConfiguration.themeColor") || "#173753" 

  useEffect(() => {
    const defaultLg = info?.localeGroups.find(o => o.default) || {}
    setSelectedLocaleGroup(defaultLg)
    if (!_.isEmpty(defaultLg)) {
      const selectedL = info?.chatConfiguration?.localised.find((o) => {
        return o.localeId === defaultLg.id
      })
      setSelectedLocalised(selectedL)
    }
  }, [JSON.stringify(info?.localeGroups), JSON.stringify(info?.chatConfiguration?.localised)])

  const header = ({
    info
  }) => {
    return (
      <Styles.Header className="chat-header"
        color={themeColor}
        open={showSettings}
        showOverflow={headerShowOverflow}
        onTransitionEnd={() => {
          if (showSettings) {
            setHeaderShowOverflow(true)
          }
        }}
      >
        <Styles.BotProfileBar
          className="chat-header-bar"
          color={themeColor}
        >
          <div className="align-left">
            <Styles.ProfileContainer className="chat-header-container">
              <Styles.ProfilePic className="chat-header-profile-pic"
                url={_.get(info, "profile.logoUrl")}
                color={themeColor}
                onClick={() => {
                  setHeaderShowProfileOverflow(!headerShowProfileOverflow)
                }}
              />
              <div 
                className="chat-header-name"
                onClick={() => {
                  setHeaderShowProfileOverflow(!headerShowProfileOverflow)
                }}
              >
                {info?.profile?.name}
              </div>
            </Styles.ProfileContainer>
          </div>
          <div className="align-right">
            <Styles.SettingsButton
              className="settings-button"
              color={themeColor}
              onClick={() => {
                if (showSettings) {
                  setHeaderShowOverflow(false)
                  setShowSettings(false)
                } else {
                  setShowSettings(true)
                }
              }}
            >
              <FontAwesomeIcon
                className="icon settings-icon"
                icon={faCog}
                fixedWidth
                spin
              />
            </Styles.SettingsButton>
          </div>

        </Styles.BotProfileBar>
        <Styles.Settings>
          <Styles.TabBar color={themeColor}>
            <Styles.Tab
              color={themeColor}
              selected={selectedTab === 0}
              onClick={() => {
                setSelectedTab(0)
              }}
            >
              <Styles.LanguageIcon color={themeColor} />
            </Styles.Tab>
            <Styles.Tab
              color={themeColor}
              selected={selectedTab === 1}
              onClick={() => {
                setSelectedTab(1)
              }}
            >
              <Styles.FontSizeIcon color={themeColor} />
            </Styles.Tab>
          </Styles.TabBar>
          {tabContent({ themeColor, localeGroups: info?.localeGroups })}
        </Styles.Settings>
      </Styles.Header>
    )
  }

  const tabContent = ({ themeColor, localeGroups }) => {
    const langOptions = (localeGroups || []).map(o => ({
      label: o.name,
      value: o.id
    }))
    const color = Color(themeColor)
    const markStyle = {
      fontSize: "0.8rem"
    }
    if (color.luminosity() <= LUM_THRESHOLD) {
      markStyle.color = "#ffffff"
    }

    switch (selectedTab) {
      case 0:
        return (
          <Styles.TabContent
            color={themeColor}
          >
            <div className="field">
              <Select
                menuPosition="fixed"
                className="lang-selector"
                options={langOptions}
                value={langOptions.find(o => o.value === selectedLocaleGroup?.id)}
                onChange={(value) => {
                  const selected = (info?.localeGroups || []).find(o => o.id === value.value)
                  setSelectedLocaleGroup(selected)
                  const selectedL = info?.chatConfiguration?.localised.find(o => o.localeId === selected.id)
                  setSelectedLocalised(selectedL)
                }}
              />
            </div>

          </Styles.TabContent>
        )
      case 1:
        return (
          <Styles.TabContent
            color={themeColor}
          >
            <div className="field">
              {/* <div className="label">Font Size</div> */}
              <Styles.SegmentedControl
                color={themeColor}
              >
                <Styles.Segment
                  selected={fontSize === "12"}
                  color={themeColor}
                  onClick={() => {
                    setFontSize("12")
                  }}
                >
                  <Styles.SFont
                    selected={fontSize === "12"}
                    color={themeColor}
                  />
                </Styles.Segment>
                <Styles.Segment
                  selected={fontSize === "16"}
                  color={themeColor}
                  onClick={() => {
                    
                    setFontSize("16")
                  }}
                >
                  <Styles.MFont
                    selected={fontSize === "16"}
                    color={themeColor}
                  />
                </Styles.Segment>
                <Styles.Segment
                  selected={fontSize === "22"}
                  color={themeColor}
                  onClick={() => {
                    setFontSize("22")
                  }}
                >
                  <Styles.LFont
                    selected={fontSize === "22"}
                    color={themeColor}
                  />
                </Styles.Segment>
              </Styles.SegmentedControl>
            </div>
          </Styles.TabContent>
        )

      default:
        return null
    }
  }

  const textInputEls = ({
    themeColor,
    persistentMenu,
  }) => {
    let result = [
      (
        <Styles.TextField
          key="text-input"
          async
          placeholder="Send a message..."
        />
      ),
      (
        <Styles.UploadButton
          key="upload-button"
          color={themeColor}
          disabled={true}
        >
          <FontAwesomeIcon className="icon" icon={faPaperclip} fixedWidth />
        </Styles.UploadButton>
      ),
      (
        <Styles.SendButton
          key="send-button"
          color={themeColor}
          disabled={true}
        >
          <FontAwesomeIcon className="icon" icon={faPaperPlane} fixedWidth />
        </Styles.SendButton>
      )
    ]
    if (persistentMenu?.enable && persistentMenu?.buttons.length >= 1) {
      result = [
        (<PersistentMenu
          color={themeColor}
          persistentMenu={persistentMenu}
          fontSize={fontSize}
        />
        ),
        ...result
      ]
    }
    return result
  }

  const footer = ({
    selectedLocalised,
    themeColor
  }) => {
    return (
      <Styles.Footer
        className="chat-footer"
        fontSize={fontSize} 
      >
        <Styles.CreateCustomerErrorContainer className="create-cutomer-error-container"></Styles.CreateCustomerErrorContainer>
        <Styles.IceBreakersContainer className="ice-breakers-container">
          {selectedLocalised?.iceBreakers?.enable && selectedLocalised?.iceBreakers?.ctas?.length && (
            <>
              {(selectedLocalised?.iceBreakers?.ctas || []).map((iceBreaker) => {
                if (iceBreaker.question) {
                  return (
                    <Styles.IceBreakerButton className="chat-ice-breaker-button" themeColor={themeColor}>
                      {iceBreaker.question}
                    </Styles.IceBreakerButton>
                  )
                }
                return null
              })}
            </>
          )}
        </Styles.IceBreakersContainer>
        <Styles.ActionRow className="chat-action" >
          <div className="action-wrapper">
            {textInputEls({ themeColor, persistentMenu: selectedLocalised?.persistentMenu })}
          </div>
        </Styles.ActionRow>
      </Styles.Footer>
    )
  }

  return (
    <>
      <Helmet>
        <style type="text/css">{`
            ${cssString}
          `}
        </style>
      </Helmet>
      <Box
        className="chat"
        sx={{
          width: "100%",
          height: "608px",
          borderRadius: "8px",
          border: "1px solid #e8e7e8"
        }}>
        <Styles.Container
          className="chat-container"
          color={themeColor}
          fontSize={fontSize}
        >
          <BusinessInfoPreview
            themeColor={themeColor}
            info={info}
            showOverflow={headerShowProfileOverflow}
            fontSize={fontSize}
            onChange={(value) => {
              setHeaderShowProfileOverflow(value)
            }}
          />
          {header({ info })}
          <div className="content">
            <Styles.Body />
            {footer({ selectedLocalised, themeColor: themeColor })}
          </div>
          
        </Styles.Container>
      </Box>
    </>
    
    
  )
}

export default WebchatPreview